import React, { useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "./contexts/AuthContext";

import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";
import SitePasswordGate from "./components/common/SitePasswordGate";
import HomePage from "./pages/HomePage";
import CheckInPage from "./pages/CheckInPage";
import NotFoundPage from "./pages/NotFoundPage";
import LoginPage from "./pages/LoginPage";
import CheckInResultPage from "./pages/CheckInResultPage";
import CostCalculatorPage from "./pages/CostCalculatorPage";
import HealthAwarenessPage from "./pages/HealthAwarenessPage";
import QuitBenefitsPage from "./pages/QuitBenefitsPage";
import SmokingStoryPage from "./pages/SmokingStoryPage";
import DashboardPage from "./pages/DashboardPage";
import ProgressTrackingPage from "./pages/ProgressTrackingPage";
import CheckInHistoryPage from "./pages/CheckInHistoryPage";
import CheckInHistoryDetailPage from "./pages/CheckInHistoryDetailPage";
import QuitPlanPage from "./pages/QuitPlanPage";

import "./App.css";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return null;
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) return <HomePage />;

  return user ? <Navigate to="/dashboard" replace /> : <HomePage />;
};

const GuestOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;

  return user ? <Navigate to="/dashboard" replace /> : children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  return user ? (
    children
  ) : (
    <Navigate to="/login" replace state={{ from: location.pathname }} />
  );
};

const AppRoutes = () => {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <Routes>
        <Route path="/" element={<HomeRedirect />} />

        <Route
          path="/login"
          element={
            <GuestOnlyRoute>
              <LoginPage />
            </GuestOnlyRoute>
          }
        />

        <Route path="/check-in" element={<CheckInPage />} />
        <Route path="/relapse" element={<CheckInPage />} />
        <Route path="/cost-calculator" element={<CostCalculatorPage />} />
        <Route path="/check-in/result" element={<CheckInResultPage />} />
        <Route path="/health-awareness" element={<HealthAwarenessPage />} />
        <Route path="/quit-benefits" element={<QuitBenefitsPage />} />
        <Route path="/smoking-story" element={<SmokingStoryPage />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/progress-tracking"
          element={<ProgressTrackingPage />}
        />

        <Route
          path="/check-in-history"
          element={
            <ProtectedRoute>
              <CheckInHistoryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/check-in/history/:resultId"
          element={
            <ProtectedRoute>
              <CheckInHistoryDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/quit-plan"
          element={
            <ProtectedRoute>
              <QuitPlanPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Footer />
    </>
  );
};

const App = () => {
  return (
    <SitePasswordGate>
      <AuthProvider>
        <div className="app">
          <AppRoutes />
        </div>
      </AuthProvider>
    </SitePasswordGate>
  );
};

export default App;
